/**
 * `returnTo` helpers — sanitization, persistence to sessionStorage, and
 * consumption. Phase 2 uses `sanitizeReturnTo` to validate the URL query
 * parameter on the sign-in page; Phase 3 uses `writeReturnTo` /
 * `readAndConsumeReturnTo` so the soft auth-reset path can preserve the
 * post-login destination without a query-string roundtrip.
 *
 * Open-redirect guard: only same-origin pathnames are accepted. Absolute
 * URLs, protocol-relative URLs, `javascript:` / `data:` schemes, control
 * characters, and oversized values are all rejected.
 *
 * @agentic connections
 */

const RETURN_TO_KEY = "connections.returnTo";
const MAX_RETURN_TO_LENGTH = 2048;

/**
 * Validate a candidate `returnTo` value. Returns the value if it is a
 * safe same-origin pathname; otherwise undefined.
 * @agentic connections
 */
export const sanitizeReturnTo = (raw: string | null): string | undefined => {
  if (!raw) return undefined;
  if (raw.length > MAX_RETURN_TO_LENGTH) return undefined;

  // Reject control characters (whitespace, tabs, newlines, etc.) anywhere
  // in the string. Spaces in legitimate paths must be percent-encoded.
  // eslint-disable-next-line no-control-regex
  if (/[\u0000-\u001f\u007f-\u009f\s]/.test(raw)) return undefined;

  // Must start with a single slash. Reject:
  //   - empty / non-slash strings
  //   - protocol-relative `//foo`
  //   - backslash-prefixed `\foo` (some browsers treat `\` like `/`)
  if (!raw.startsWith("/")) return undefined;
  if (raw.startsWith("//")) return undefined;
  if (raw.startsWith("/\\")) return undefined;

  // Reject anything that parses as an absolute URL (i.e. contains a
  // scheme). The leading-slash check above already covers most cases,
  // but parse with a fake base to be defensive.
  try {
    const url = new URL(raw, "https://example.invalid");
    if (url.origin !== "https://example.invalid") return undefined;
    return url.pathname + url.search + url.hash;
  } catch {
    return undefined;
  }
};

/**
 * Persist the current location's pathname + search + hash to
 * sessionStorage so the post-sign-in flow can restore it. Idempotent and
 * safe to call when sessionStorage is unavailable (Safari private mode).
 * @agentic connections
 */
export const writeReturnTo = (location: {
  pathname: string;
  search: string;
  hash: string;
}): void => {
  const value = location.pathname + location.search + location.hash;
  const sanitized = sanitizeReturnTo(value);
  if (!sanitized) return;
  try {
    sessionStorage.setItem(RETURN_TO_KEY, sanitized);
  } catch {
    // sessionStorage unavailable (e.g. Safari private mode); silently skip.
    // The user lands on `/` after sign-in; acceptable degradation.
  }
};

/**
 * Read the persisted `returnTo`, removing it from sessionStorage in the
 * same call so it is only consumed once. Returns undefined if absent or
 * if the value fails sanitization.
 * @agentic connections
 */
export const readAndConsumeReturnTo = (): string | undefined => {
  let raw: string | null = null;
  try {
    raw = sessionStorage.getItem(RETURN_TO_KEY);
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    return undefined;
  }
  return sanitizeReturnTo(raw);
};

/**
 * Test-only escape hatch for clearing the persisted `returnTo`.
 * @agentic connections
 */
export const _clearReturnToForTesting = (): void => {
  try {
    sessionStorage.removeItem(RETURN_TO_KEY);
  } catch {
    // Ignore.
  }
};
